<template>
  <Layout title="" description="">
    <div class="container-fluid py-4">
      <!-- HEADER -->
      <div class="mb-4">
        <h1 class="fw-bold mb-1">Create Invoice</h1>
        <p class="text-muted mb-0">Generate invoice with preview and job reference</p>
      </div>

      <!-- FULL PAGE LOADING OVERLAY -->
      <div
        v-if="isActionLoading"
        class="invoice-loading-overlay"
      >
        <div class="invoice-loading-card">
          <div class="invoice-spinner"></div>
          <div class="fw-semibold">
            {{ actionLoadingText }}
          </div>
          <div class="text-muted small mt-1">
            Please wait, do not close this page.
          </div>
        </div>
      </div>

      <!-- ================= TOP SECTION ================= -->
      <div class="row g-4 mb-4">
        <!-- LEFT: CANVAS PREVIEW -->
        <div class="col-12 col-xl-6">
          <div class="bg-white border rounded-4 shadow-sm p-4 h-100">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 class="fw-bold mb-1">Invoice Preview</h5>
                <p class="text-muted small mb-0">Live preview of invoice drawing</p>
              </div>
            </div>

            <div class="text-center">
              <canvas
                ref="invoiceCanvas"
                class="border rounded bg-white w-100"
                style="max-width: 650px;"
              />
            </div>

            <!-- PAGE NAV -->
            <div class="mt-3 d-flex justify-content-center align-items-center gap-2 flex-wrap">
              <button
                class="btn btn-outline-secondary btn-sm"
                @click="prevPreviewPage"
                :disabled="previewPage <= 1"
                type="button"
              >
                ← Prev
              </button>

              <span class="text-muted small">
                Previewing Page {{ previewPage }} of {{ paginatedItems.length || 1 }}
              </span>

              <button
                class="btn btn-outline-secondary btn-sm"
                @click="nextPreviewPage"
                :disabled="previewPage >= paginatedItems.length"
                type="button"
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        <!-- RIGHT: JOB REFERENCE -->
        <div class="col-12 col-xl-6">
          <InvoiceJobReference
            :clients="clients"
            @select-job="handleSelectJob"
          />
        </div>
      </div>

      <!-- ================= FORM SECTION ================= -->
      <div class="bg-white border rounded-4 shadow-sm p-4">
        <div class="mb-3">
          <h5 class="fw-bold mb-1">Invoice Form</h5>
          <p class="text-muted small mb-0">Fill invoice details below</p>
        </div>

        <!-- HEADER FIELDS -->
        <div class="row g-3 mb-4">
          <div class="col-md-6">
            <label class="form-label">Invoice No</label>
            <input
              v-model="form.invoiceNo"
              class="form-control form-control-sm"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">Date</label>
            <input
              v-model="form.date"
              type="date"
              class="form-control form-control-sm"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">Terms</label>
            <input
              v-model="form.terms"
              class="form-control form-control-sm"
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">Vessel</label>
            <input
              v-model="form.vessel"
              class="form-control form-control-sm"
            />
          </div>
        </div>

        <!-- BILL TO -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
            <h6 class="fw-bold mb-0">Bill To</h6>

            <button
              class="btn btn-outline-primary btn-sm"
              type="button"
              @click="openCompanyFormModal()"
              :disabled="isActionLoading"
            >
              + Add Company Info
            </button>
          </div>

          <!-- QUICK FILL COMPANY -->
          <div class="border rounded-4 p-3 bg-light mb-3">
            <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
              <div>
                <div class="fw-semibold small text-dark">
                  Quick Fill From Saved Companies
                </div>
                <div class="text-muted small">
                  Click a company card to fill Bill To.
                </div>
              </div>

              <button
                type="button"
                @click="fetchCompanies"
                class="btn btn-outline-secondary btn-sm"
                :disabled="isActionLoading || loadingCompanies"
              >
                {{ loadingCompanies ? 'Loading...' : 'Refresh' }}
              </button>
            </div>

            <input
              v-model="companySearch"
              class="form-control form-control-sm mb-3"
              placeholder="Search saved company e.g. Tuas, ABC Marine, admin@email.com"
              :disabled="isActionLoading"
            />

            <div
              v-if="loadingCompanies"
              class="text-muted small"
            >
              Loading saved companies...
            </div>

            <div
              v-else-if="!filteredCompanies.length"
              class="rounded bg-white border p-3 text-muted small"
            >
              No saved companies found yet.
            </div>

            <div
              v-else
              class="d-flex gap-2 overflow-auto pb-1 quick-scroll"
            >
              <button
                v-for="company in filteredCompanies"
                :key="company.id"
                type="button"
                @click="applyCompanyQuickFill(company)"
                class="company-template-card text-start bg-white border rounded-3 p-3"
                :disabled="isActionLoading"
              >
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div class="min-w-0 flex-grow-1">
                    <div class="fw-semibold text-dark small line-clamp-2">
                      {{ company.companyName || 'No company name' }}
                    </div>

                    <div class="text-muted company-card-line mt-1 line-clamp-2">
                      {{ company.companyAddress || 'No address' }}
                    </div>

                    <div class="company-card-line mt-1 text-muted">
                      {{ company.contactNumber || '-' }}
                    </div>

                    <div class="company-card-line text-muted line-clamp-1">
                      {{ company.email || '-' }}
                    </div>
                  </div>

                  <div class="d-flex flex-column gap-1" @click.stop>
                    <button
                      class="btn btn-outline-secondary btn-xs"
                      type="button"
                      @click="openCompanyFormModal(company)"
                      :disabled="isActionLoading"
                    >
                      Edit
                    </button>

                    <button
                      class="btn btn-outline-danger btn-xs"
                      type="button"
                      @click="openDeleteCompanyModal(company)"
                      :disabled="isActionLoading"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Company Name</label>
              <input
                v-model="form.billToLine1"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label">Company Address</label>
              <input
                v-model="form.billToLine2"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label">Contact Number</label>
              <input
                v-model="form.billToLine3"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label">Email</label>
              <input
                v-model="form.billToLine4"
                class="form-control form-control-sm"
              />
            </div>
          </div>
        </div>

        <!-- LINE ITEMS -->
        <div class="mt-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="fw-bold mb-0">Line Items</h6>
            <button
              class="btn btn-outline-primary btn-sm"
              @click="addItem"
              type="button"
            >
              + Add Row
            </button>
          </div>

          <div
            v-for="(item, index) in items"
            :key="index"
            class="border rounded p-3 mb-3 bg-light"
          >
            <div class="row g-3">
              <div class="col-md-2">
                <label class="form-label">Date</label>
                <input
                  v-model="item.date"
                  type="date"
                  class="form-control form-control-sm"
                />
              </div>

              <div class="col-md-3">
                <label class="form-label">Voucher No</label>
                <div class="input-group input-group-sm">
                  <input
                    v-model="item.voucherNo"
                    class="form-control"
                  />
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="validateVoucher(item)"
                  >
                    Check
                  </button>
                </div>
              </div>

              <div class="col-md-5">
                <label class="form-label">Description</label>
                <input
                  v-model="item.description"
                  class="form-control form-control-sm"
                />
              </div>

              <div class="col-md-2">
                <label class="form-label">Amount</label>
                <input
                  v-model="item.amount"
                  type="number"
                  step="0.01"
                  class="form-control form-control-sm"
                />
              </div>
            </div>

            <div class="text-end mt-2" v-if="items.length > 1">
              <button
                class="btn btn-outline-danger btn-sm"
                @click="removeItem(index)"
                type="button"
              >
                Remove
              </button>
            </div>
          </div>

          <div class="mt-3 text-end fw-bold fs-6">
            Total: $ {{ totalAmount }}
          </div>

          <div class="text-muted small text-end mt-1">
            Invoice Pages: {{ paginatedItems.length }}
          </div>
        </div>

        <!-- CREDIT NOTES -->
        <div class="mt-4">
          <h6 class="fw-bold">Credit Notes (PDF)</h6>

          <div
            v-for="(fileObj, index) in creditNotes"
            :key="index"
            class="d-flex gap-2 mb-2 align-items-center"
          >
            <input
              type="file"
              accept="application/pdf"
              class="form-control form-control-sm"
              @change="onCreditNoteChange($event, index)"
            />

            <button
              class="btn btn-danger btn-sm"
              @click="removeCreditNote(index)"
              type="button"
            >
              Remove
            </button>
          </div>

          <button
            class="btn btn-outline-primary btn-sm"
            type="button"
            @click="addCreditNote"
          >
            + Add Credit Note
          </button>
        </div>

        <!-- ACTIONS -->
        <div class="text-end mt-4">
          <button
            class="btn btn-primary btn-sm"
            @click="saveInvoice"
            :disabled="saving"
          >
            {{ saving ? 'Saving...' : 'Save Invoice' }}
          </button>
        </div>
      </div>

      <!-- ADD / EDIT COMPANY MODAL -->
      <div
        v-if="showCompanyFormModal"
        class="modal-backdrop-custom"
      >
        <div class="modal-card">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <h5 class="fw-bold mb-1">
                {{ editingCompanyId ? 'Edit Company' : 'Add Company Info' }}
              </h5>
              <p class="text-muted small mb-0">
                All fields can be empty.
              </p>
            </div>

            <button
              type="button"
              class="btn-close"
              @click="closeCompanyFormModal"
              :disabled="savingCompany"
            ></button>
          </div>

          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Company Name</label>
              <input
                v-model="companyForm.companyName"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-12">
              <label class="form-label">Company Address</label>
              <textarea
                v-model="companyForm.companyAddress"
                rows="3"
                class="form-control form-control-sm"
              ></textarea>
            </div>

            <div class="col-md-6">
              <label class="form-label">Contact Number</label>
              <input
                v-model="companyForm.contactNumber"
                class="form-control form-control-sm"
              />
            </div>

            <div class="col-md-6">
              <label class="form-label">Email</label>
              <input
                v-model="companyForm.email"
                type="email"
                class="form-control form-control-sm"
              />
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-4">
            <button
              class="btn btn-outline-secondary btn-sm"
              type="button"
              @click="closeCompanyFormModal"
              :disabled="savingCompany"
            >
              Cancel
            </button>

            <button
              class="btn btn-primary btn-sm"
              type="button"
              @click="saveCompany"
              :disabled="savingCompany"
            >
              {{ savingCompany ? 'Saving...' : editingCompanyId ? 'Update Company' : 'Create Company' }}
            </button>
          </div>
        </div>
      </div>

      <!-- CONFIRM MODAL -->
      <div
        v-if="confirmModal.show"
        class="modal-backdrop-custom"
      >
        <div class="modal-card modal-card-sm">
          <h5 class="fw-bold mb-3">
            {{ confirmModal.title }}
          </h5>

          <p class="text-muted mb-4 white-space-pre-line">
            {{ confirmModal.message }}
          </p>

          <div class="d-flex justify-content-end gap-2">
            <button
              class="btn btn-outline-secondary btn-sm"
              type="button"
              @click="closeConfirmModal"
              :disabled="confirmModal.loading"
            >
              Cancel
            </button>

            <button
              class="btn btn-sm"
              type="button"
              :class="confirmModal.danger ? 'btn-danger' : 'btn-dark'"
              @click="confirmModal.onConfirm"
              :disabled="confirmModal.loading"
            >
              {{ confirmModal.loading ? 'Processing...' : confirmModal.confirmText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from '@/components/Layout.vue'
import InvoiceJobReference from '@/components/InvoiceJobReference.vue'
import { useToast } from 'vue-toastification'
import axios from 'axios'
import { getAuth, onAuthStateChanged } from 'firebase/auth'
import router from '@/router'

export default {
  name: 'InvoiceCreatePage',
  components: { Layout, InvoiceJobReference },

  setup() {
    return { toast: useToast() }
  },

  data() {
    return {
      invoiceBg: require('@/assets/invoice.png'),
      searchInvoiceNo: '',
      token: null,
      saving: false,
      createdInvoiceId: '',
      previewPage: 1,
      clients: [],

      companies: [],
      companySearch: '',
      companySearchTimeout: null,
      loadingCompanies: false,
      savingCompany: false,
      deletingCompany: false,

      showCompanyFormModal: false,
      editingCompanyId: null,

      companyForm: {
        companyName: '',
        companyAddress: '',
        contactNumber: '',
        email: ''
      },

      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        loading: false,
        danger: false,
        onConfirm: null,
        onCancel: null
      },

      form: {
        invoiceNo: '',
        date: '',
        terms: '',
        attn: '',
        billToLine1: '',
        billToLine2: '',
        billToLine3: '',
        billToLine4: '',
        vessel: ''
      },

      creditNotes: [{ file: null }],

      items: [
        {
          date: '',
          voucherNo: '',
          description: '',
          amount: ''
        }
      ]
    }
  },

  computed: {
    isActionLoading() {
      return (
        this.saving ||
        this.loadingCompanies ||
        this.savingCompany ||
        this.deletingCompany
      )
    },

    actionLoadingText() {
      if (this.saving) return 'Saving invoice...'
      if (this.savingCompany) return 'Saving company...'
      if (this.deletingCompany) return 'Deleting company...'
      if (this.loadingCompanies) return 'Loading companies...'
      return 'Processing...'
    },

    totalAmount() {
      return this.items
        .reduce((sum, item) => sum + Number(item.amount || 0), 0)
        .toFixed(2)
    },

    paginatedItems() {
      const chunkSize = 7
      const pages = []

      for (let i = 0; i < this.items.length; i += chunkSize) {
        pages.push(this.items.slice(i, i + chunkSize))
      }

      return pages.length ? pages : [[]]
    },

    filteredCompanies() {
      const keyword = String(this.companySearch || '').trim().toLowerCase()

      if (!keyword) return this.companies

      return this.companies.filter(company => {
        const text = [
          company.companyName,
          company.companyAddress,
          company.contactNumber,
          company.email
        ]
          .join(' ')
          .toLowerCase()

        return text.includes(keyword)
      })
    }
  },

  async mounted() {
    await this.checkAuthState()

    await Promise.all([
      this.fetchClients(),
      this.fetchCompanies()
    ])

    this.$nextTick(() => {
      this.drawInvoicePreview()
    })
  },

  watch: {
    form: {
      handler() {
        this.drawInvoicePreview()
      },
      deep: true
    },

    items: {
      handler() {
        if (this.previewPage > this.paginatedItems.length) {
          this.previewPage = this.paginatedItems.length || 1
        }

        this.drawInvoicePreview()
      },
      deep: true
    },

    companySearch() {
      this.handleCompanySearch()
    }
  },

  methods: {
    async getAuthHeader() {
      const user = getAuth().currentUser
      if (!user) throw new Error('Not logged in')

      const token = await user.getIdToken()
      return { Authorization: token }
    },

    async checkAuthState() {
      const auth = getAuth()

      return new Promise((resolve, reject) => {
        onAuthStateChanged(auth, async user => {
          if (user) {
            try {
              this.token = await user.getIdToken()
              resolve()
            } catch (err) {
              reject(err)
            }
          } else {
            router.push('/')
            reject(new Error('No user logged in'))
          }
        })
      })
    },

    async fetchClients() {
      try {
        const headers = await this.getAuthHeader()
        const res = await axios.get(`${this.$apiURL}/clients`, { headers })
        this.clients = res.data && res.data.clients ? res.data.clients : []
      } catch (err) {
        console.error(err)
        this.clients = []
      }
    },

    async fetchCompanies() {
      try {
        this.loadingCompanies = true

        const headers = await this.getAuthHeader()
        const params = {}

        if (this.companySearch && this.companySearch.trim()) {
          params.search = this.companySearch.trim()
        }

        const res = await axios.get(
          `${this.$apiURL}/api/companies`,
          {
            headers,
            params
          }
        )

        this.companies = res.data && res.data.companies ? res.data.companies : []
      } catch (err) {
        console.error(err)
        this.companies = []
        this.toast.error('Failed to load companies')
      } finally {
        this.loadingCompanies = false
      }
    },

    handleCompanySearch() {
      if (this.companySearchTimeout) {
        clearTimeout(this.companySearchTimeout)
      }

      this.companySearchTimeout = setTimeout(async () => {
        await this.fetchCompanies()
      }, 400)
    },

    openCompanyFormModal(company) {
      if (company) {
        this.editingCompanyId = company.id

        this.companyForm = {
          companyName: company.companyName || '',
          companyAddress: company.companyAddress || '',
          contactNumber: company.contactNumber || '',
          email: company.email || ''
        }
      } else {
        this.editingCompanyId = null

        this.companyForm = {
          companyName: '',
          companyAddress: '',
          contactNumber: '',
          email: ''
        }
      }

      this.showCompanyFormModal = true
    },

    closeCompanyFormModal() {
      if (this.savingCompany) return

      this.showCompanyFormModal = false
      this.editingCompanyId = null

      this.companyForm = {
        companyName: '',
        companyAddress: '',
        contactNumber: '',
        email: ''
      }
    },

    async saveCompany() {
      try {
        this.savingCompany = true

        const headers = await this.getAuthHeader()

        const payload = {
          companyName: this.companyForm.companyName || '',
          companyAddress: this.companyForm.companyAddress || '',
          contactNumber: this.companyForm.contactNumber || '',
          email: this.companyForm.email || ''
        }

        if (this.editingCompanyId) {
          await axios.put(
            `${this.$apiURL}/api/companies/${this.editingCompanyId}`,
            payload,
            { headers }
          )

          this.toast.success('Company updated')
        } else {
          await axios.post(
            `${this.$apiURL}/api/companies`,
            payload,
            { headers }
          )

          this.toast.success('Company created')
        }

        await this.fetchCompanies()
        this.closeCompanyFormModal()
      } catch (err) {
        console.error(err)
        this.toast.error('Failed to save company')
      } finally {
        this.savingCompany = false
      }
    },

    applyCompanyQuickFill(company) {
      this.form.billToLine1 = company.companyName || ''
      this.form.billToLine2 = company.companyAddress || ''
      this.form.billToLine3 = company.contactNumber || ''
      this.form.billToLine4 = company.email || ''

      this.toast.success('Company info filled')
    },

    openDeleteCompanyModal(company) {
      this.openConfirmModal({
        title: 'Delete Company',
        message: `Are you sure you want to delete "${company.companyName || 'No company name'}"?`,
        confirmText: 'Delete',
        danger: true,
        onConfirm: async () => {
          await this.deleteCompany(company)
        }
      })
    },

    async deleteCompany(company) {
      if (!company || !company.id) return

      try {
        this.deletingCompany = true
        this.confirmModal.loading = true

        const headers = await this.getAuthHeader()

        await axios.delete(
          `${this.$apiURL}/api/companies/${company.id}`,
          { headers }
        )

        this.toast.success('Company deleted')

        this.closeConfirmModal()
        await this.fetchCompanies()
      } catch (err) {
        console.error(err)
        this.toast.error('Failed to delete company')
      } finally {
        this.deletingCompany = false
        this.confirmModal.loading = false
      }
    },

    openConfirmModal({ title, message, confirmText, danger, onConfirm, onCancel }) {
      this.confirmModal = {
        show: true,
        title,
        message,
        confirmText: confirmText || 'Confirm',
        loading: false,
        danger: Boolean(danger),
        onConfirm,
        onCancel: onCancel || null
      }
    },

    closeConfirmModal() {
      if (this.confirmModal.loading) return

      const onCancel = this.confirmModal.onCancel

      this.confirmModal = {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        loading: false,
        danger: false,
        onConfirm: null,
        onCancel: null
      }

      if (onCancel) onCancel()
    },

    getClientName(clientId) {
      if (!clientId) return ''
      if (String(clientId).toLowerCase() === 'admin') return 'Admin'

      const c = this.clients.find(cl => String(cl.id) === String(clientId))

      return c ? c.contactPerson || c.name || c.companyName || '' : ''
    },

    handleSelectJob(job) {
      this.form.vessel = job.vesselName || ''

      const clientName = this.getClientName(job.clientId)

      if (clientName) {
        this.form.billToLine1 = clientName
      }

      this.form.attn = this.getCrewDisplayName(job)

      this.items = job.vouchers && job.vouchers.length
        ? job.vouchers.map(v => ({
            date: job.startTime ? job.startTime.slice(0, 10) : '',
            voucherNo: v,
            description: `Transport - ${job.vesselName || job.id}`,
            amount: ''
          }))
        : [
            {
              date: job.startTime ? job.startTime.slice(0, 10) : '',
              voucherNo: '',
              description: `Transport - ${job.vesselName || job.id}`,
              amount: ''
            }
          ]
    },

    getCrewDisplayName(b) {
      if (b.crewName && b.crewName.trim() !== '') return b.crewName

      if (Array.isArray(b.crewMembers) && b.crewMembers.length > 0) {
        return b.crewMembers.map(c => c.name).filter(Boolean).join(', ')
      }

      return ''
    },

    addItem() {
      this.items.push({
        date: '',
        voucherNo: '',
        description: '',
        amount: ''
      })
    },

    removeItem(index) {
      this.items.splice(index, 1)
    },

    addCreditNote() {
      this.creditNotes.push({ file: null })
    },

    removeCreditNote(index) {
      this.creditNotes.splice(index, 1)
    },

    onCreditNoteChange(e, index) {
      const file = e.target.files[0]

      if (!file) return

      if (file.type !== 'application/pdf') {
        this.toast.error('Only PDF allowed')
        return
      }

      this.creditNotes[index].file = file
    },

    prevPreviewPage() {
      if (this.previewPage > 1) {
        this.previewPage--
        this.drawInvoicePreview()
      }
    },

    nextPreviewPage() {
      if (this.previewPage < this.paginatedItems.length) {
        this.previewPage++
        this.drawInvoicePreview()
      }
    },

    drawInvoicePreview() {
      if (this.previewPage > this.paginatedItems.length) {
        this.previewPage = this.paginatedItems.length || 1
      }

      if (this.previewPage < 1) {
        this.previewPage = 1
      }

      const pageItems = this.paginatedItems[this.previewPage - 1] || []

      this.drawInvoicePageToCanvas(
        this.$refs.invoiceCanvas,
        pageItems,
        this.previewPage,
        this.paginatedItems.length
      )
    },

    async generateInvoicePageBlobs() {
      const blobs = []

      for (let i = 0; i < this.paginatedItems.length; i++) {
        const pageItems = this.paginatedItems[i]
        const canvas = document.createElement('canvas')

        await this.drawInvoicePageToCanvas(
          canvas,
          pageItems,
          i + 1,
          this.paginatedItems.length
        )

        const blob = await new Promise(resolve => {
          canvas.toBlob(resolve, 'image/png')
        })

        blobs.push(blob)
      }

      return blobs
    },

    async drawInvoicePageToCanvas(canvas, pageItems, currentPage = 1, totalPages = 1) {
      if (!canvas) return

      const ctx = canvas.getContext('2d')

      if (!ctx) return

      const img = new Image()
      img.src = this.invoiceBg

      await new Promise(resolve => {
        img.onload = resolve
      })

      canvas.width = img.width
      canvas.height = img.height

      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(img, 0, 0)

      // HEADER
      ctx.fillStyle = '#000'
      ctx.font = '12px Arial'
      ctx.fillText(this.form.invoiceNo || '', 620, 154)
      ctx.fillText(this.formatDate(this.form.date), 620, 174)
      ctx.fillText(this.form.terms || '', 620, 190)

      // BILL TO
      let z = 140
      const lineSpacing = 22
      const maxChars = 40

      const billLines = [
        this.form.billToLine1,
        this.form.billToLine2,
        this.form.billToLine3,
        this.form.billToLine4
      ]

      billLines.forEach(text => {
        if (!text) return

        for (let i = 0; i < text.length; i += maxChars) {
          const chunk = text.substring(i, i + maxChars)
          ctx.fillText(chunk, 80, z)
          z += lineSpacing
        }
      })

      // ATTN / VESSEL
      ctx.font = '12px Arial'
      ctx.fillText(this.form.attn || '', 100, 252)

      ctx.fillStyle = 'white'
      ctx.fillRect(42, 240, 45, 12)

      ctx.font = '10px Arial'
      ctx.fillStyle = 'black'
      ctx.fillText('VESSEL', 43, 252)

      ctx.font = '12px Arial'
      ctx.fillText(this.form.vessel || '', 100, 252)

      // ITEMS
      let y = 300

      pageItems.forEach(item => {
        if (item.date || item.voucherNo || item.description || item.amount) {
          ctx.font = '12px Arial'
          ctx.fillText(this.formatDate(item.date), 45, y)

          ctx.font = '10px Arial'
          ctx.fillText(item.voucherNo || '', 115, y)

          ctx.font = '12px Arial'
          ctx.fillText(this.truncateText(item.description || '', 45), 190, y)

          const amountText = item.amount ? Number(item.amount).toFixed(2) : ''
          ctx.fillText(amountText, 550, y)

          y += 40
        }
      })

      // TOTAL
      ctx.font = '12px Arial'
      const isLastPage = currentPage === totalPages

      if (isLastPage) {
        ctx.fillText(`$ ${this.totalAmount}`, 550, 600)
      } else {
        ctx.font = '10px Arial'
        ctx.fillText('B/F to next Page', 543, 600)
      }

      // PAGE NO
      ctx.font = '10px Arial'
      ctx.fillText(`Page ${currentPage} of ${totalPages}`, 600, 780)
    },

    async checkDuplicateInvoiceNo(invoiceNo, excludeId) {
      try {
        const headers = await this.getAuthHeader()
        const params = { invoiceNo }

        if (excludeId) {
          params.excludeId = excludeId
        }

        const res = await axios.get(
          `${this.$apiURL}/api/invoices/check-number`,
          { headers, params }
        )

        return res.data || { exists: false, count: 0, matches: [] }
      } catch (err) {
        // A failed pre-check must not block saving; the API still guards it.
        console.error(err)
        return { exists: false, count: 0, matches: [] }
      }
    },

    confirmDuplicateInvoiceNo(invoiceNo, check) {
      const matches = check && check.matches ? check.matches : []

      const lines = matches
        .slice(0, 5)
        .map(m => {
          const amount = Number(m.totalAmount || 0).toFixed(2)
          const when = this.formatDate(m.date) || 'no date'
          return `- ${when} : $ ${amount}`
        })
        .join('\n')

      const more = matches.length > 5
        ? `\n...and ${matches.length - 5} more`
        : ''

      const plural = matches.length === 1 ? '' : 's'

      const message = [
        `Invoice number "${invoiceNo}" is already used by `,
        `${matches.length} other invoice${plural}:\n\n`,
        `${lines}${more}\n\n`,
        'Save anyway?'
      ].join('')

      return new Promise(resolve => {
        this.openConfirmModal({
          title: 'Duplicate Invoice Number',
          message,
          confirmText: 'Save Anyway',
          danger: true,
          onConfirm: () => {
            this.confirmModal.onCancel = null
            this.closeConfirmModal()
            resolve(true)
          },
          onCancel: () => resolve(false)
        })
      })
    },

    async readBlobError(err) {
      // Errors on a responseType:'blob' request arrive as a Blob, not JSON.
      try {
        const data = err && err.response ? err.response.data : null
        if (!data) return null
        if (typeof data.text !== 'function') return data

        return JSON.parse(await data.text())
      } catch (e) {
        return null
      }
    },

    truncateText(text, maxLength = 45) {
      if (!text) return ''
      return text.length > maxLength ? text.slice(0, maxLength) : text
    },

    formatDate(dateStr) {
      if (!dateStr) return ''

      const [year, month, day] = dateStr.split('-')

      if (!year || !month || !day) return dateStr

      return `${day}/${month}/${year}`
    },

    async saveInvoice() {
      if (!this.form.invoiceNo) {
        this.toast.warning('Invoice number required')
        return
      }

      if (!this.form.date) {
        this.toast.warning('Invoice date required')
        return
      }

      // Invoice numbers are no longer unique by construction, so warn before
      // creating a second invoice carrying one that is already in use.
      const duplicateCheck = await this.checkDuplicateInvoiceNo(this.form.invoiceNo)

      if (duplicateCheck.exists) {
        const confirmed = await this.confirmDuplicateInvoiceNo(
          this.form.invoiceNo,
          duplicateCheck
        )

        if (!confirmed) return
      }

      try {
        this.saving = true

        const headers = await this.getAuthHeader()
        const pageBlobs = await this.generateInvoicePageBlobs()

        const formData = new FormData()

        const payload = {
          invoiceNo: this.form.invoiceNo,
          date: this.form.date,
          terms: this.form.terms,
          attn: this.form.attn,
          vessel: this.form.vessel,
          billTo: {
            line1: this.form.billToLine1,
            line2: this.form.billToLine2,
            line3: this.form.billToLine3,
            line4: this.form.billToLine4
          },
          items: this.items
        }

        formData.append('data', JSON.stringify(payload))

        if (duplicateCheck.exists) {
          formData.append('allowDuplicate', 'true')
        }

        pageBlobs.forEach((blob, index) => {
          formData.append(
            'invoicePages',
            blob,
            `${this.form.invoiceNo}_page_${index + 1}.png`
          )
        })

        this.creditNotes.forEach((f) => {
          if (f.file) {
            formData.append('creditNotes', f.file)
          }
        })

        const res = await axios.post(
          `${this.$apiURL}/api/invoices`,
          formData,
          {
            headers,
            responseType: 'blob'
          }
        )

        const blobPdf = new Blob([res.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blobPdf)

        const a = document.createElement('a')
        a.href = url
        a.download = `${this.form.invoiceNo}.pdf`
        document.body.appendChild(a)
        a.click()
        a.remove()

        window.URL.revokeObjectURL(url)

        this.createdInvoiceId = res.headers['x-invoice-id'] || ''

        this.toast.success('Invoice created & downloaded 🎉')
      } catch (err) {
        console.error(err)

        const parsed = await this.readBlobError(err)

        if (parsed && parsed.code === 'DUPLICATE_INVOICE_NO') {
          this.toast.error(parsed.error)
        } else {
          this.toast.error('Error saving invoice')
        }
      } finally {
        this.saving = false
      }
    },

    async validateVoucher(item) {
      if (!item.voucherNo) {
        this.toast.warning('Enter voucher number')
        return
      }

      try {
        const headers = await this.getAuthHeader()

        const res = await axios.get(
          `${this.$apiURL}/api/vouchers/check/${item.voucherNo}`,
          { headers }
        )

        if (res.data && res.data.exists) {
          this.toast.success('Voucher exists ✅')
        } else {
          this.toast.error('Voucher not found ❌')
        }
      } catch (err) {
        console.error(err)
        this.toast.error('Error checking voucher')
      }
    },

    async downloadInvoice() {
      if (!this.searchInvoiceNo) {
        this.toast.warning('Enter invoice number')
        return
      }

      try {
        const headers = await this.getAuthHeader()

        const res = await axios.get(
          `${this.$apiURL}/api/invoices/download/${this.searchInvoiceNo}`,
          {
            headers,
            responseType: 'blob'
          }
        )

        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `${this.searchInvoiceNo}.pdf`)
        document.body.appendChild(link)
        link.click()
        link.remove()

        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error(err)
        this.toast.error('Invoice not found ❌')
      }
    }
  }
}
</script>

<style scoped>
.invoice-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.invoice-loading-card {
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: 18px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
}

.invoice-spinner {
  width: 34px;
  height: 34px;
  border: 4px solid #e5e7eb;
  border-top-color: #111827;
  border-radius: 50%;
  margin: 0 auto 12px;
  animation: invoice-spin 0.8s linear infinite;
}

@keyframes invoice-spin {
  to {
    transform: rotate(360deg);
  }
}

.company-template-card {
  min-width: 240px;
  max-width: 280px;
  flex-shrink: 0;
  transition: 0.15s ease;
}

.company-template-card:hover {
  background: #f8f9fa !important;
  border-color: #111827 !important;
}

.company-template-card:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.company-card-line {
  font-size: 12px;
  line-height: 1.35;
}

.btn-xs {
  padding: 2px 8px;
  font-size: 11px;
  line-height: 1.4;
}

.quick-scroll::-webkit-scrollbar {
  height: 6px;
}

.quick-scroll::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 9999px;
}

.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-card {
  width: 100%;
  max-width: 620px;
  background: #fff;
  border-radius: 18px;
  padding: 24px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.25);
  max-height: 90vh;
  overflow-y: auto;
}

.modal-card-sm {
  max-width: 460px;
}

.white-space-pre-line {
  white-space: pre-line;
}
</style>